import React, { createContext, useState, useContext, useEffect } from 'react';

import { LoginInterface } from 'src/models/Login';
import { LoginApiResponse } from 'src/models/types';
import { UserInterface } from 'src/models/User';
import { login } from 'src/services/AuthenticationService';
import { getProfile, setProfile, setUserUpdateCallback } from 'src/utils/auth';

interface AuthContextProps {
  user: UserInterface | undefined | null;
  setUser: (user: UserInterface) => void;
  refreshUser: () => void;
  isAuthenticated: boolean;
  signIn: (loginData: LoginInterface) => Promise<LoginApiResponse>
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

type AuthProviderProps = {
  children: React.ReactNode;
};

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<UserInterface | undefined | null>(() => getProfile());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    const profile = getProfile();
    if (profile) {
      setIsAuthenticated(true);
      setUserState(profile);
    } else {
      setIsAuthenticated(false);
      setUserState(null);
    }
  }, []);

  const setUser = (userData: UserInterface) => {
    setProfile(userData);
    setUserState(userData);
  };

  const refreshUser = () => {
    const profile = getProfile();
    setUserState(profile);
    if (profile) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  };

  // Register the refresh callback so updateUserAllocationRights can trigger a re-render
  useEffect(() => {
    setUserUpdateCallback(refreshUser);
    return () => {
      setUserUpdateCallback(null);
    };
  }, []);

  async function signIn({ email, password }: LoginInterface) {
    const response = await login({ email, password });

    if (response.data) {
      refreshUser();
    }

    return response;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, refreshUser, isAuthenticated, signIn }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuthContext };